/**
 * Length Role - Validate Pillar
 *
 * Validação semântica de valores de comprimento.
 * Zero dependências externas - pode ser usado standalone.
 *
 * @example
 * import { validateLength, isValidLength } from '@attrx/role-morphic/length/validate';
 *
 * validateLength(100);   // { valid: true, errors: [] }
 * validateLength(-5);    // { valid: false, errors: ['Length cannot be negative'] }
 * isValidLength(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface LengthValidationConfig {
  min?: number;
  max?: number;
  integer?: boolean;
  minError?: string;
  maxError?: string;
}

// =============================================================================
// CONFIG
// =============================================================================

/**
 * Configuração padrão de validação para comprimento
 * Length é magnitude escalar, portanto não-negativo por padrão
 */
export const LENGTH_VALIDATION_CONFIG: LengthValidationConfig = {
  min: 0,
  minError: "Length cannot be negative",
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de comprimento
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns Resultado da validação com erros se houver
 *
 * @example
 * validateLength(100);                    // { valid: true, errors: [] }
 * validateLength(-5);                     // { valid: false, errors: ['Length cannot be negative'] }
 * validateLength(-5, { min: undefined }); // { valid: true, errors: [] } - permite negativo
 */
export function validateLength(
  value: unknown,
  config: LengthValidationConfig = LENGTH_VALIDATION_CONFIG
): ValidationResult {
  const errors: string[] = [];

  // Type check
  if (typeof value !== "number") {
    return { valid: false, errors: ["Value must be a number"] };
  }

  // Finite check
  if (!Number.isFinite(value)) {
    return { valid: false, errors: ["Value must be finite"] };
  }

  // Min check
  if (config.min !== undefined && value < config.min) {
    const msg = config.minError ?? `Value must be at least ${config.min}`;
    errors.push(msg);
  }

  // Max check
  if (config.max !== undefined && value > config.max) {
    const msg = config.maxError ?? `Value must be at most ${config.max}`;
    errors.push(msg);
  }

  // Integer check
  if (config.integer && !Number.isInteger(value)) {
    errors.push("Value must be an integer");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Verifica se um valor de comprimento é válido
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidLength(100);  // true
 * isValidLength(-5);   // false
 */
export function isValidLength(
  value: unknown,
  config: LengthValidationConfig = LENGTH_VALIDATION_CONFIG
): boolean {
  return validateLength(value, config).valid;
}
